import { OnboardingStatus } from '../../services/onboardingService';

export type StepKey = 'company' | 'taxcode' | 'testkey' | 'firstcall';
export type StepState = 'done' | 'next' | 'todo';

export interface ActivationStep {
  key: StepKey;
  title: string;
  note: string;
  done: boolean;
  state: StepState;
  actionLabel: string;
  to: string;
}

/**
 * The four steps from a fresh account to a first tax calculation.
 * `state` is derived: done, the first unfinished step, then everything after it.
 */
export const buildActivationSteps = (status: OnboardingStatus | null): ActivationStep[] => {
  const companyId = status?.company_id ?? null;
  const api = status?.api;

  const raw: Omit<ActivationStep, 'state'>[] = [
    {
      key: 'company',
      title: 'Add your company details',
      note: 'Legal name, address and tax ID. They appear on every calculation and report.',
      done: Boolean(status) && !status?.company_is_default,
      actionLabel: companyId ? 'Open company details' : 'Add a company',
      to: companyId ? `/companies/${companyId}/edit` : '/companies/new',
    },
    {
      key: 'taxcode',
      title: 'Choose what you sell',
      note: 'A product tax code tells each state whether your product is taxable, and at what rate.',
      done: Boolean(status?.company_has_tax_code),
      actionLabel: 'Set a tax code',
      to: companyId ? `/company/${companyId}/tax-codes` : '/companies',
    },
    {
      key: 'testkey',
      title: 'Create a test API key',
      note: 'Test keys run on the full engine and are never billed. Copy the key when it is shown; it is not shown again.',
      done: Boolean(api?.has_key),
      actionLabel: 'Create a test key',
      to: '/developers/console',
    },
    {
      key: 'firstcall',
      title: 'Send your first calculation',
      note: 'Paste one request with your key. A 200 response means tax is coming back for a real cart.',
      done: Boolean(api?.first_call_done),
      actionLabel: 'Copy the command',
      to: '/developers/console',
    },
  ];

  const firstOpen = raw.findIndex((step) => !step.done);
  return raw.map((step, index) => ({
    ...step,
    state: step.done ? 'done' : index === firstOpen ? 'next' : 'todo',
  }));
};

export const activationProgress = (steps: ActivationStep[]) => {
  const done = steps.filter((step) => step.done).length;
  return { done, total: steps.length, percent: steps.length ? Math.round((done / steps.length) * 100) : 0 };
};

export const activationComplete = (status: OnboardingStatus | null): boolean =>
  buildActivationSteps(status).every((step) => step.done);

export const missingCompanyFields = (status: OnboardingStatus | null): string[] => {
  const map: Record<string, string> = {
    name: 'legal company name',
    address: 'business address',
    tax_id: 'tax ID (EIN)',
  };
  return (status?.company_missing_fields || []).map((field) => map[field] || field);
};
